//@Version: 1.0.2
import {inject, Injectable} from '@angular/core';
import {TranslateLoader} from '@ngx-translate/core';
import {map, switchMap} from 'rxjs/operators';
import {Observable, forkJoin} from 'rxjs';
import { IVersion, ResponseDesignColor, ResponseDesignTypography, ResponseTranslateText, SharedOpenapiCastService} from 'build/project/openapi-cast/shared/shared-openapi-cast.service';
import {DESIGN, DESIGN_TYPOGRAPHY_PROPERTY, DESIGNLANG, DESIGNSESSION, DESIGNTYPOGRAPHY} from './design.model';
import { JsonHelpersService } from '../helpers/json-helpers.service';

@Injectable({
   providedIn: 'root'
})
export class DesignService implements TranslateLoader {

   public sSharedOpenapi = inject(SharedOpenapiCastService);
   private sJsonHelpers_ = inject(JsonHelpersService);

   private option_: string = DESIGN.OPTION;
   private initTranslate: object = {};
   private versionListDesign: IVersion = {} as IVersion;
   

   get option (): string {
      return this.option_;
   }

   constructor () { 
   }

   public setOption (paramPos?: number): string {
      const href = window.location.href;
      const keyvalue = href.split('?')[1];
      if (keyvalue && keyvalue.indexOf('option=') > -1) {
         this.option_ = keyvalue.substring(keyvalue.indexOf('option=') + 7, keyvalue.indexOf('&', keyvalue.indexOf('option=')) == -1 ? keyvalue.length : keyvalue.indexOf('&', keyvalue.indexOf('option=')));
         sessionStorage.setItem(DESIGNSESSION.OPTION, this.option_);
         return this.option_;
      } else {
         if (paramPos && paramPos > -1) {
            const hash = window.location.hash;
            const param = hash.split('/');

            sessionStorage.setItem(DESIGNSESSION.OPTION, param[paramPos]);
            return param[paramPos];
         } else {
            const sessionOpt = sessionStorage.getItem(DESIGNSESSION.OPTION);
            return sessionOpt && sessionOpt.length > 0 ? sessionOpt : DESIGN.OPTION;
         }
      }
   }

   public getTranslation (lang: string): Observable<object> {
      const newProjectname = sessionStorage.getItem(DESIGNSESSION.PROJECTNAME) ?? undefined;

      return ((!this.sSharedOpenapi.config 
         ?  this.sSharedOpenapi.setConfigs().pipe(
               switchMap(_ => this.loadTextAndDesign(lang, newProjectname))
            )
         :  this.loadTextAndDesign(lang, newProjectname))).pipe(
               map(item => {
                  this.initTranslate = Object.assign(this.initTranslate, item);
                  return this.initTranslate
               })
            )
   }

   private loadTextAndDesign (lang: string, newProjectname: string | undefined): Observable<object> {
      const defaultValue: boolean = lang == DESIGNLANG.DEFAULT ? true : false;
      let loadData: Observable<object | boolean>[] = [
         this.sSharedOpenapi.loadTranslateText(defaultValue ? undefined : newProjectname, this.option_, defaultValue),
         this.sSharedOpenapi.loadTranslateConfig(defaultValue ? undefined : newProjectname, this.option_, defaultValue)
      ];         

      if (!defaultValue) {
         loadData.push(this.loadTypography(newProjectname)); 
         loadData.push(this.loadColor(newProjectname)); 
      }

      if (!this.sSharedOpenapi.config.extern) loadData.push(this.getConfigIntern());

      return forkJoin(loadData).pipe(
         map((data) => this.mergeObjects([(<object[]>data[0])].concat(<object[]>data[1])))
      );
   }

   

   private mergeObjects (dataList: object[]): object {
      let res = {};
      dataList.forEach(obj => Object.assign(res, this.sJsonHelpers_.flatten(Array.isArray(obj) ? this.transformTranslateText(obj) : obj)));
      return this.sJsonHelpers_.unflatten(res);
   }

   private transformTranslateText (translateTextList: ResponseTranslateText[]): object {
      let result = translateTextList.map(element => {
         let key = element.key!;
         let obj = {} as { [key: string]: any };
         obj[key as keyof typeof obj] = element.value!;
         return obj;
      });
      return this.sJsonHelpers_.unflatten(this.sJsonHelpers_.arrayToObject(this.sJsonHelpers_.flatten(result)));
   }

   private loadTypography (newProjectname: string | undefined): Observable<boolean> {
      return this.sSharedOpenapi.loadDesignTypography(newProjectname, this.option_).pipe(
         map((res: ResponseDesignTypography[]) => {
            return (typeof res == 'object' && Array.isArray(res)) ? res && res.length > 0 ? this.setTypography(res) : true : false;
         })
      );
   }

   private setTypography (typographyList: ResponseDesignTypography[]): boolean {
      Object.values(DESIGNTYPOGRAPHY).forEach((typokey) => {
         this.setTypographyProperty(typographyList, typokey);
      });

      return true;
   }

   private setTypographyProperty (typographyList: ResponseDesignTypography[], key: string): boolean {
      Object.values(DESIGN_TYPOGRAPHY_PROPERTY).forEach((typokey) => {
         if (typographyList.find(typo => typo.key == key + typokey)) {
            document.documentElement.style.setProperty('--' + key + typokey, typographyList.find(typo => typo.key == key + typokey)!.value!);
         }
      });

      return true;
   }

   private loadColor (newProjectname: string | undefined): Observable<boolean> {
      return this.sSharedOpenapi.loadDesignColor(newProjectname, this.option_).pipe(
         map((res: ResponseDesignTypography[]) => (typeof res == 'object' && Array.isArray(res)) ? res && res.length > 0 ? this.setColor(res) : true : false)
      );
   }

   private setColor (colorList: ResponseDesignColor[]): boolean {
      colorList.forEach(color => {
         document.documentElement.style.setProperty('--' + color.key.replaceAll('.', '-'), color.value!);
      });

      return true;
   }

   private getConfigIntern (): Observable<object> {
      return this.sSharedOpenapi.getConfigFile();
   }
}